import React, { useEffect, useState } from 'react';
import API from '../../api/api';
import { useUser } from '../../context/UserContext';
import { FaBell, FaTrash, FaChevronRight } from 'react-icons/fa';
import { motion } from 'framer-motion';

interface Notification {
  id: string;
  title: string;
  body: string;
  click_action: string;
  type: string;
  boardId: string;
  sender: string;
  timestamp: {
    _seconds: number;
    _nanoseconds: number;
  };
}

const Notifications: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useUser();

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await API.get('/notifications');
        const data = response.data;

        if (data?.data) {
          setNotifications(data.data);
        } else {
          setNotifications([]);
        }
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load notifications');
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, []);

  const timeAgo = (seconds: number) => {
    const date = new Date(seconds * 1000);
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diff < 60) return `${diff} second${diff !== 1 ? 's' : ''} ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)} minute${Math.floor(diff / 60) !== 1 ? 's' : ''} ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hour${Math.floor(diff / 3600) !== 1 ? 's' : ''} ago`;
    return `${Math.floor(diff / 86400)} day${Math.floor(diff / 86400) !== 1 ? 's' : ''} ago`;
  };

  const handleClearAll = async () => {
    try {
      if (!user) {
        throw new Error('User not found');
      }

      const response = await API.delete(`/notifications/${user.id}`);

      if (response.status === 200) {
        setNotifications([]);
      } else {
        console.error("Failed to clear notifications");
      }
    } catch (error) {
      console.error("Error clearing notifications:", error);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'invite':
        return '👋';
      case 'alert':
        return '⚠️';
      case 'update':
        return '🔄';
      default:
        return '🔔';
    }
  };

  return (
    <div className="container my-4" style={{ maxWidth: '800px' }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <FaBell className="text-primary me-2" size={24} />
          <h4 className="mb-0 fw-bold">Notifications</h4>
          {notifications.length > 0 && (
            <span className="badge bg-primary ms-2">
              {notifications.length}
            </span>
          )}
        </div>
        {notifications.length > 0 && (
          <button 
            className="btn btn-outline-danger btn-sm d-flex align-items-center"
            onClick={handleClearAll}
          >
            <FaTrash className="me-1" size={14} />
            Clear All
          </button>
        )}
      </div>

      {loading ? (
        <div className="d-flex justify-content-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger d-flex align-items-center">
          <div>
            <strong>Error loading notifications:</strong> {error}
          </div>
        </div>
      ) : notifications.length === 0 ? (
        <div className="text-center py-5">
          <div className="text-muted mb-3">
            <FaBell size={48} className="opacity-25" />
          </div>
          <h5 className="text-muted">No notifications yet</h5>
          <p className="text-muted">We'll notify you when there's something new</p>
        </div>
      ) : (
        <div className="list-group">
          {notifications.map((notif) => (
            <motion.div
              key={notif.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="list-group-item list-group-item-action mb-3 border-0 rounded-3 shadow-sm"
              style={{
                borderLeft: `4px solid ${
                  notif.type === 'alert' ? '#dc3545' : 
                  notif.type === 'invite' ? '#198754' : '#0d6efd'
                }`
              }}
            >
              <div className="d-flex">
                <div className="me-3 fs-4">
                  {getNotificationIcon(notif.type)}
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex w-100 justify-content-between">
                    <h5 className="mb-1 fw-semibold">{notif.title}</h5>
                    <small className="text-muted">
                      {timeAgo(notif.timestamp._seconds)}
                    </small>
                  </div>
                  <p className="mb-2 text-muted">{notif.body}</p>
                  {notif.click_action && (
                    <div className="mt-2">
                      <button className="btn btn-sm btn-outline-primary d-flex align-items-center">
                        {notif.click_action}
                        <FaChevronRight className="ms-1" size={12} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Notifications;